import type { FC } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiGallection } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ExcerptRenderer from '@wix/da-shared-react/pkg/TextRenderer/ExcerptRenderer';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import GallectionTiptapViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/Gallection';
import {
  isEditorTextEmpty,
  getWordCount,
} from '@wix/da-editor-tiptap/pkg/components/utils/helpers';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';

import s from './FolderDescription.scss';

const COLLAPSE_HEIGHT = 137;
const COLLAPSE_HEIGHT_MOBILE = 110;

export interface Props {
  folder?: PapiGallection;
  className?: string;
  onExpand?: (toggle: boolean) => void;
}

const FolderDescription: FC<Props> = ({
  folder,
  className,
  onExpand,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const wordCount = getWordCount(folder?.richDescription);

  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const [collapsedDescription, setCollapsedDescription] = useState<
    boolean | undefined
  >(undefined);

  useEffect(() => {
    if (
      (isMobile &&
        (descriptionRef.current?.clientHeight ?? 0) > COLLAPSE_HEIGHT_MOBILE) ||
      (descriptionRef.current?.clientHeight ?? 0) > COLLAPSE_HEIGHT
    ) {
      setCollapsedDescription(true);
    } else {
      setCollapsedDescription(undefined);
    }
  }, [isMobile, wordCount]);

  if (folder?.richDescription && isEditorTextEmpty(folder?.richDescription)) {
    return null;
  }
  return (
    (<div
      className={cn(
        s['root'],
        !collapsedDescription && s['scrollable'],
        className
      )}
    >
      <div
        className={cn(
          s['collapsible-description'],
          collapsedDescription === true && s['collapsed-description'],
          collapsedDescription === false && s['expanded-description']
        )}
      >
        <div ref={descriptionRef}>
          {folder?.richDescription?.html.type === 'writer' && (
            <ExcerptRenderer
              htmlExcerptLegacy
              legacyTheme={'comments'}
              textContent={folder.richDescription}
            />
          )}
          {folder?.richDescription?.html.type === 'tiptap' && (
            <TextRenderer
              tipTapPresetId="gallection"
              tipTapViewer={GallectionTiptapViewer}
              cookieType={MeasuredCookieType.GALLECTION_FOLDER}
              textContent={folder.richDescription}
            />
          )}
        </div>
      </div>
      {(collapsedDescription === true || collapsedDescription === false) && (
        <PlainButtonOrLink
          className={s['expand-btn']}
          onClick={() => {
            setCollapsedDescription(!collapsedDescription);
            onExpand && onExpand(!!collapsedDescription);
          }}
          biData={
            collapsedDescription
              ? BiData<DefaultBiEvent>({
                  evid: 2,
                  itemid: folder?.folderId,
                  typeid: gallectionTypeToResourceType(folder?.type || ''),
                  click_info: `${folder?.type}_folder_description_show_more`,
                })
              : undefined
          }
        >
          {t(!collapsedDescription ? 'common.showLess' : 'common.showMore')}
        </PlainButtonOrLink>
      )}
      {(!descriptionRef.current || collapsedDescription === undefined) && (
        /* CLS placeholder stops the elements below jumping between SSR and measurements */
        (<div style={{ height: '20px' }}></div>)
      )}
    </div>)
  );
};
FolderDescription.displayName = 'FolderDescription';

export default FolderDescription;
